<template>
    <!-- Tarjeta de un alojamiento en Mis espacios (individual o unidad de un
         edificio). Las acciones que cambian datos las resuelve la página. -->
    <article
        :class="['bk-sp', { 'is-leaving': estadoTarjeta.removing, 'is-busy': estadoTarjeta.cloning && !temporal, 'is-creating': temporal, 'is-new': estadoTarjeta.nuevo, 'is-copy': !!estadoTarjeta.copiaDe, 'is-pickable': seleccionable && !temporal, 'is-picked': seleccionable && elegido }]"
        :data-sp="acc.idalojamiento"
    >
        <!-- "Editar varios": toda la tarjeta pasa a ser una casilla -->
        <button v-if="seleccionable && !temporal" type="button" class="bk-sp-pick" role="checkbox" :aria-checked="String(elegido)" :aria-label="$t('editor_espacio.ms.choose', { x: nombre, id: acc.idalojamiento })" @click="$emit('elegir', acc.idalojamiento)">
            <span class="bk-sp-pick-c" aria-hidden="true"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"><path d="m5 12.5 4.5 4.5L19 7.5"/></svg></span>
        </button>
        <!-- Copia hecha en esta visita: etiqueta con el original, para distinguirla
             (el nombre y las fotos son iguales). -->
        <p v-if="estadoTarjeta.copiaDe" class="bk-sp-copy" role="status">
            <span v-html="ICON.copy"></span>
            {{ temporal ? $t('nuevo_menu.sp.copying') : $t('nuevo_menu.sp.copy_of', { id: estadoTarjeta.copiaDe }) }}
        </p>
        <div class="bk-sp-media">
            <router-link v-if="!temporal" :to="`/alojamiento/${acc.idalojamiento}`" target="_blank" tabindex="-1" aria-hidden="true">
                <CarouselList :idAlojamiento="acc.idalojamiento" :externalReference="acc.external_reference" :photos="acc.gallery || []" />
            </router-link>
            <!-- Copia que se está creando (todavía sin id real): sin pedir fotos a la API. -->
            <span v-else class="bk-sp-noimg"><span class="bk-spinner" aria-hidden="true" style="border-color: rgba(232,100,10,.25); border-top-color: #e8640a; width: 30px; height: 30px;"></span></span>
            <span :class="['bk-badge', aprobado ? 'bk-badge--ok' : 'bk-badge--wait']">{{ $t(aprobado ? 'nuevo_menu.sp.approved' : 'nuevo_menu.sp.review') }}</span>
            <span v-if="modo" class="bk-sp-tag">{{ $t(`nuevo_menu.sp.mode_${modo}`) }}</span>
        </div>
        <div class="bk-sp-body">
            <!-- ID del alojamiento (siempre) y código de carga / sincronización (si tiene).
                 Clic = copiar. -->
            <div v-if="!temporal" class="bk-sp-ids">
                <button type="button" class="bk-sp-id bk-sp-id--main" :aria-label="$t('nuevo_menu.sp.copy_id', { id: acc.idalojamiento })" @click="copiar('id', acc.idalojamiento)">
                    <span class="bk-sp-id-k">ID</span>{{ acc.idalojamiento }}<span class="bk-sp-id-ok" aria-live="polite">{{ copiado === 'id' ? '✓ ' + $t('nuevo_menu.copied') : '' }}</span>
                </button>
                <button v-if="codigo" type="button" class="bk-sp-id" :aria-label="$t('nuevo_menu.sp.copy_code', { codigo })" @click="copiar('codigo', codigo)">
                    <span class="bk-sp-id-k">{{ $t('nuevo_menu.sp.code') }}</span>{{ codigo }}<span class="bk-sp-id-ok" aria-live="polite">{{ copiado === 'codigo' ? '✓ ' + $t('nuevo_menu.copied') : '' }}</span>
                </button>
            </div>
            <router-link class="bk-sp-name" :to="`/alojamiento/${acc.idalojamiento}`" target="_blank">{{ nombre }}</router-link>
            <p v-if="direccion" class="bk-sp-addr"><span v-html="ICON.pin"></span>{{ direccion }}</p>
            <p v-if="precio" class="bk-sp-price">{{ precio }} <small>/ {{ $t(modo === 'diario' ? 'accommodation.per-day' : 'accommodation.per-month') }}</small></p>
        </div>
        <div class="bk-sp-acts">
            <button type="button" :class="['bk-ib', { 'is-on': estadoTarjeta.fav }]" :aria-pressed="String(estadoTarjeta.fav)"
                :aria-label="`${$t(estadoTarjeta.fav ? 'nuevo_menu.sp.fav_off' : 'nuevo_menu.sp.fav_on')}: ${nombre}`"
                :data-tip="$t(estadoTarjeta.fav ? 'nuevo_menu.sp.fav_off' : 'nuevo_menu.sp.fav_on')"
                @click="emitir('fav')" v-html="estadoTarjeta.fav ? ICON.heartOn : ICON.heart"></button>
            <button type="button" class="bk-ib" :aria-label="`${$t('nuevo_menu.sp.share')}: ${nombre}`" :data-tip="$t('nuevo_menu.sp.share')" @click="emitir('compartir')" v-html="ICON.share"></button>
            <span class="bk-spacer"></span>
            <router-link :to="`/bloquear/${acc.idalojamiento}`" class="bk-ib" :aria-label="`${$t('my-accommodations.date-lock')}: ${nombre}`" :data-tip="$t('my-accommodations.date-lock')"><span v-html="ICON.cal"></span></router-link>
            <router-link :to="`/editar-alojamiento/${acc.idalojamiento}`" class="bk-ib" :aria-label="`${$t('my-accommodations.edit')}: ${nombre}`" :data-tip="$t('my-accommodations.edit')"><span v-html="ICON.edit"></span></router-link>
            <button type="button" class="bk-ib" :aria-label="`${$t('my-accommodations.clone')}: ${nombre}`" :data-tip="$t('my-accommodations.clone')" @click="emitir('duplicar')" v-html="ICON.copy"></button>
            <button type="button" class="bk-ib bk-ib--danger" :aria-label="`${$t('my-accommodations.remove-accommodation')}: ${nombre}`" :data-tip="$t('my-accommodations.remove-accommodation')" @click="emitir('eliminar')" v-html="ICON.trash"></button>
        </div>
    </article>
</template>

<script>
import CarouselList from '@/components/Carousel/CarouselList';
import ICON from './iconos';

export default {
    name: 'SpCard',
    components: { CarouselList },
    props: {
        acc: { type: Object, required: true },
        direccion: { type: String, default: '' },
        estadoTarjeta: { type: Object, default: () => ({}) },
        seleccionable: { type: Boolean, default: false },
        elegido: { type: Boolean, default: false },
    },
    data() {
        return { ICON, copiado: null, copiadoTimer: null };
    },
    computed: {
        temporal() {
            return String(this.acc.idalojamiento).indexOf('temp_') === 0;
        },
        codigo() {
            const c = this.acc.idcodigo;
            return c == null || String(c).trim() === '' ? '' : String(c).trim();
        },
        aprobado() {
            return this.acc.aprobado === 1 || this.acc.aprobado === '1';
        },
        nombre() {
            try {
                const j = JSON.parse(this.acc.nombre);
                return j[this.$i18n.locale] || j.es || j.en || Object.values(j)[0] || '';
            } catch (e) {
                return this.acc.nombre || '';
            }
        },
        // diario | mensual (ambos se muestra por mes, como antes)
        modo() {
            const m = this.acc.modalidad_tiempo;
            return m === 'diario' ? 'diario' : (m === 'mensual' || m === 'ambos' ? 'mensual' : '');
        },
        precio() {
            const filtro = this.$options.filters && this.$options.filters.convertPriceToInteger;
            const monto = this.modo === 'diario' ? this.acc.arriendo_diario : this.acc.arriendo;
            if (!filtro || monto == null || monto === '') return '';
            return filtro({ amount: monto, currency: this.acc.moneda });
        },
    },
    methods: {
        emitir(accion) {
            this.$emit('accion', { accion, acc: this.acc });
        },
        async copiar(cual, valor) {
            const texto = String(valor);
            try {
                await navigator.clipboard.writeText(texto);
            } catch (e) {
                const ta = document.createElement('textarea');
                ta.value = texto; ta.style.position = 'fixed'; ta.style.opacity = '0';
                document.body.appendChild(ta); ta.select();
                try { document.execCommand('copy'); } catch (e2) { /* sin permiso */ }
                ta.remove();
            }
            this.copiado = cual;
            clearTimeout(this.copiadoTimer);
            this.copiadoTimer = setTimeout(() => { this.copiado = null; }, 1600);
        },
    },
    beforeDestroy() {
        clearTimeout(this.copiadoTimer);
    },
};
</script>

<style>
/* ID y código: arriba del nombre, en mono, clic para copiar. */
.bk-sp-ids { display: flex; flex-wrap: wrap; gap: 6px; margin: 0 0 4px; }
.bk-sp-id {
    display: inline-flex; align-items: center; gap: 6px; min-height: 28px; padding: 0 10px; margin: 0;
    border: 1px solid var(--bk-line-2); border-radius: 8px; background: #faf8f6; cursor: copy;
    font: 600 13px/1 ui-monospace, SFMono-Regular, Menlo, Consolas, monospace; color: var(--bk-ink-2);
    font-variant-numeric: tabular-nums; transition: border-color .15s, background .15s;
}
.bk-sp-id:hover { border-color: var(--bk-orange); background: var(--bk-orange-50); }
.bk-sp-id--main { border-color: transparent; background: var(--bk-ink); color: #fff; }
.bk-sp-id--main:hover { border-color: transparent; background: #3b3530; }
.bk-sp-id-k { font: 700 10.5px/1 'DM Sans', system-ui, sans-serif; letter-spacing: .06em; text-transform: uppercase; opacity: .65; }
.bk-sp-id-ok { font: 600 11.5px/1 'DM Sans', system-ui, sans-serif; color: var(--bk-green); }
.bk-sp-id--main .bk-sp-id-ok { color: #7ee2b5; }
.bk-sp-id-ok:empty { display: none; }
.bk-sp-media > a { display: block; height: 100%; }
.bk-sp-acts a.bk-ib > span, .bk-sp-addr > span:first-child, .bk-sp-copy > span { display: contents; }
.bk-sp-media .no-photos { display: grid; place-items: center; font-size: 13px; color: var(--bk-muted); }
/* Modo "Editar varios" (diseño de la demo Desktop/nuevo-editaralojamiento) */
.bk-sp.is-pickable { position: relative; cursor: pointer; }
.bk-sp-pick { position: absolute; inset: 0; z-index: 4; width: 100%; height: 100%; padding: 0; margin: 0; border: 0; border-radius: inherit; background: transparent; cursor: pointer; }
.bk-sp-pick:focus-visible { outline: none; box-shadow: 0 0 0 4px rgba(255, 122, 0, .3); }
.bk-sp-pick-c { position: absolute; right: 12px; top: 12px; display: grid; place-items: center; width: 30px; height: 30px; border-radius: 50%; background: rgba(255, 255, 255, .95); border: 1.5px solid var(--bk-line-2); color: transparent; box-shadow: 0 4px 12px -4px rgba(29, 26, 23, .3); transition: background .15s, border-color .15s; }
.bk-sp-pick-c svg { width: 17px; height: 17px; }
.bk-sp.is-pickable .bk-sp-acts { opacity: .45; }
.bk-sp.is-pickable .bk-sp-tag { visibility: hidden; }
.bk-sp.is-picked { box-shadow: 0 0 0 3px var(--bk-orange), 0 30px 80px -40px rgba(122, 57, 0, .28); }
.bk-sp.is-picked .bk-sp-pick-c { background: var(--bk-orange); border-color: var(--bk-orange); color: #fff; }
</style>
